import { create } from 'zustand';
import apiClient from '../services/api';
import { setPagination, setNoteStateChange, setPendingTopicCheck, getPendingTopicCheck } from '../hooks/useDashboardGlobalVar';

// 定义分页限制常量，从环境变量读取，默认20
const pageLimit = parseInt(process.env.REACT_APP_FETCH_LIMIT || process.env.FETCH_LIMIT || '20', 10);

// 辅助函数：从笔记列表中收集 (kbId, topicId) 对并设置到全局状态
const collectTopicsFromNotes = (notes) => {
  if (!notes || notes.length === 0) return;

  const topicMap = {}; // { kbId: Set<topicId> }

  notes.forEach(note => {
    // 从 topicMappings 中获取 topic_id 和 knowledge_base_id
    if (note.topicMappings && Array.isArray(note.topicMappings)) {
      note.topicMappings.forEach(mapping => {
        const topicId = mapping.topic_id;
        const kbId = mapping.knowledge_base_id || 'private'; // 如果没有 kb_id，则认为是私有库

        if (topicId) {
          if (!topicMap[kbId]) {
            topicMap[kbId] = new Set();
          }
          topicMap[kbId].add(topicId);
        }
      });
    }
  });

  // 如果有主题需要检查，合并到现有的 pendingTopicCheck
  if (Object.keys(topicMap).length > 0) {
    const existingCheck = getPendingTopicCheck() || {};
    const mergedCheck = { ...existingCheck };

    for (const [kbId, topicIds] of Object.entries(topicMap)) {
      if (!mergedCheck[kbId]) {
        mergedCheck[kbId] = [];
      }
      // 合并并去重
      const existingSet = new Set(mergedCheck[kbId]);
      topicIds.forEach(id => existingSet.add(id));
      mergedCheck[kbId] = Array.from(existingSet);
    }

    console.log('[noteStore] 收集到的主题需要检查:', mergedCheck);
    setPendingTopicCheck(mergedCheck, 'noteStore.collectTopicsFromNotes');
  }
};

export const useNoteStore = create((set, get) => ({
  notes: [],
  pinnedNotes: [],
  currentNote: null,
  isLoading: false,
  error: null,
  pagination: {
    total: 0,
    page: 1,
    limit: pageLimit,
    hasMore: false
  },
  _activeRequests: {}, // 用于跟踪活跃的请求，防止重复调用

  // 重置状态
  resetState: () => {
    const resetPagination = {
      total: 0,
      page: 1,
      limit: pageLimit,
      hasMore: false
    };

    set({
      notes: [],
      pinnedNotes: [],
      currentNote: null,
      isLoading: false,
      error: null,
      pagination: resetPagination,
      _activeRequests: {},
    });

    // 同步更新全局分页状态
    setPagination(resetPagination, 'noteStore.resetState');
  },

  // 从URL提取内容
  extractFromUrl: async (url) => {
    set({ isLoading: true, error: null });
    try {
      //const response = await apiClient.post('/notes/extract-from-url', { url });

      // 确保 URL 是完整的 HTTP(S) URL，确保发送给 Python 服务的 URL 是符合 pydantic.HttpUrl 验证要求的格式。
      const fullUrl = url.startsWith('http') ? url : `https://${url}`;
      const response = await apiClient.post('/notes/extract-from-url', { url: fullUrl });

      return { success: true, data: response.data };
    } catch (error) {
      set({
        isLoading: false,
        error: error.response?.data?.message || 'Failed to extract content from URL',
      });
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to extract content from URL, please check if the URL is valid'
      };
    } finally {
      set({ isLoading: false });
    }
  },

  // 生成摘要
  generateSummary: async (content) => {
    set({ isLoading: true, error: null });
    const startTime = Date.now();
    console.log('[前端] 开始生成摘要请求');
    try {
      const response = await apiClient.post('/notes/generate-summary', { content });
      const endTime = Date.now();
      console.log(`[前端] 摘要生成完成，总耗时: ${endTime - startTime}ms`);
      return { success: true, data: { summary: response.data.summary } };
    } catch (error) {
      const endTime = Date.now();
      console.error(`[前端] 摘要生成失败，耗时: ${endTime - startTime}ms`, error.message);
      set({
        isLoading: false,
        error: error.response?.data?.message || 'Failed to generate summary, error: ' + error.message,
      });
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to generate summary, error: ' + error.message
      };
    } finally {
      set({ isLoading: false });
    }
  },

  // 获取笔记列表
  fetchNotes: async (page = 1, limit = pageLimit, tags = [], reset = true, topicId = null, isPrivate = false, filterType = null, cacheBreaker = null, kbId = null) => {
    const startTime = Date.now();

    // 生成请求键用于防止重复请求
    const requestKey = `fetchNotes_${page}_${limit}_${tags.join(',')}_${topicId}_${isPrivate}_${filterType}_${cacheBreaker}_${kbId}`;

    // 检查是否已有相同请求正在进行
    const { _activeRequests } = get();
    if (_activeRequests && _activeRequests[requestKey]) {
      console.log('[前端] 相同请求正在进行中，跳过重复请求:', requestKey);
      return;
    }

    set(state => ({
      ...state,
      isLoading: true,
      error: null,
      _activeRequests: {
        ...state._activeRequests,
        [requestKey]: true
      }
    }));

    try {
      const tagsQuery = tags.length > 0 ? `&tags=${tags.join(',')}` : '';
      const topicQuery = topicId ? `&topicId=${topicId}` : '';
      const privateQuery = isPrivate ? `&isPrivate=true` : '';
      const filterQuery = filterType ? `&filterType=${filterType}` : '';
      const cacheBreakerQuery = cacheBreaker ? `&cb=${cacheBreaker}` : '';
      const kbIdQuery = kbId ? `&kbId=${kbId}` : '';
      const url = `/notes?page=${page}&limit=${limit}${tagsQuery}${topicQuery}${privateQuery}${filterQuery}${cacheBreakerQuery}${kbIdQuery}&_t=${Date.now()}`;

      console.log('[前端] 发送API请求:', {
        url,
        method: 'GET',
        requestKey,
        kbId: kbId,
        kbIdType: typeof kbId,
        kbIdLength: kbId ? kbId.length : 'N/A'
      });

      const response = await apiClient.get(url);

      console.log('[前端] 原始API响应:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        dataType: typeof response.data,
        hasItems: response.data && 'items' in response.data,
        itemsType: response.data?.items ? typeof response.data.items : 'undefined',
        itemsLength: response.data?.items ? response.data.items.length : 'N/A',
        topicId: topicId
      });

      const { items, total } = response.data;

      console.log('[前端] 解构后的数据:', {
        itemsLength: items ? items.length : 'null/undefined',
        total: total,
        topicId: topicId,
        expectedHasMore: total > (page * limit)
      });

      const pinnedNotes = items.filter(note => note.is_pinned);
      const otherNotes = items.filter(note => !note.is_pinned);

      const endTime = Date.now();
      const duration = endTime - startTime;

      console.log('[前端] API响应成功:', {
        执行时间: `${duration}ms`,
        总记录数: total,
        返回记录数: items.length,
        置顶笔记数: pinnedNotes.length,
        普通笔记数: otherNotes.length,
        filterType,
        reset,
        page,
        timestamp: new Date().toISOString()
      });

      // 记录返回的笔记详情
      console.log('[前端] 返回的笔记列表:', items.map(note => ({
        id: note.id,
        title: note.title.substring(0, 30),
        owner_id: note.owner_id,
        // is_public字段已不再使用
        is_pinned: note.is_pinned,
        topic_ids: note.topicMappings ? note.topicMappings.map(m => m.topic_id) : [],
        tags: note.tags?.map(t => t.name) || []
      })));

      if (reset || page === 1) {
        console.log('[前端] 重置笔记列表 (第一页或重新搜索)');
        // 重置列表（第一页或者重新搜索）
        const resetPagination = {
          total,
          page,
          limit,
          hasMore: total > (page * limit),
          isLoading: false
        };

        set({
          notes: otherNotes,
          pinnedNotes,
          isLoading: false,
          pagination: resetPagination,
        });

        // 同步更新全局分页状态
        setPagination(resetPagination, 'noteStore.fetchNotes.reset');
      } else {
        console.log('[前端] 追加笔记到现有列表 (无限滚动)');
        // 追加到现有列表（无限滚动）
        const currentState = get();

        // 前端去重处理：合并时去除重复的笔记
        const existingNoteIds = new Set([...currentState.notes, ...currentState.pinnedNotes].map(note => note.id));
        const newOtherNotes = otherNotes.filter(note => !existingNoteIds.has(note.id));
        const newPinnedNotes = pinnedNotes.filter(note => !existingNoteIds.has(note.id));

        console.log('[前端] 去重处理:', {
          原有笔记数: currentState.notes.length,
          新笔记数: otherNotes.length,
          去重后新笔记数: newOtherNotes.length,
          原有置顶数: currentState.pinnedNotes.length,
          新置顶数: pinnedNotes.length,
          去重后新置顶数: newPinnedNotes.length
        });

        const newPagination = {
          total,
          page,
          limit,
          hasMore: total > (page * limit),
          isLoading: false
        };

        set({
          notes: [...currentState.notes, ...newOtherNotes],
          pinnedNotes: page === 1 ? [...currentState.pinnedNotes, ...newPinnedNotes] : currentState.pinnedNotes, // 置顶笔记只在第一页加载
          isLoading: false,
          pagination: newPagination,
        });

        // 同步更新全局分页状态
        setPagination(newPagination, 'noteStore.fetchNotes');
      }

      console.log('[前端] fetchNotes完成:', {
        最终状态: {
          notes数量: otherNotes.length,
          pinnedNotes数量: pinnedNotes.length,
          总数: total,
          当前页: page
        },
        requestKey,
        timestamp: new Date().toISOString()
      });

      // 收集笔记主题用于自动刷新检查
      collectTopicsFromNotes(items);

    } catch (error) {
      const endTime = Date.now();
      const duration = endTime - startTime;

      console.error('[前端] fetchNotes失败:', {
        执行时间: `${duration}ms`,
        error: error.message,
        response: error.response?.data,
        status: error.response?.status,
        requestKey,
        timestamp: new Date().toISOString()
      });

      const errorPagination = {
        ...get().pagination,
        isLoading: false
      };

      set(state => ({
        ...state,
        isLoading: false,
        error: error.response?.data?.message || 'Failed to fetch notes',
        pagination: errorPagination,
        _activeRequests: {
          ...state._activeRequests,
          [requestKey]: false
        }
      }));

      // 同步更新全局分页状态
      setPagination(errorPagination, 'noteStore.fetchNotes.error');
    } finally {
      // 清除活跃请求标记
      set(state => ({
        ...state,
        _activeRequests: {
          ...state._activeRequests,
          [requestKey]: false
        }
      }));
    }
  },

  // 加载更多笔记
  loadMoreNotes: async (tags = [], topicId = null, isPrivate = false, filterType = null, kbId = null) => {
    const { pagination, isLoading } = get();

    if (isLoading || pagination.isLoading) {
      console.log('[loadMoreNotes] 正在加载中，跳过重复请求');
      return; // 避免重复加载
    }

    const nextPage = pagination.page + 1;
    const totalPages = Math.ceil(pagination.total / pagination.limit);

    console.log('[loadMoreNotes] 分页信息:', { nextPage, totalPages, current: pagination.page });

    if (nextPage > totalPages) {
      console.log('[loadMoreNotes] 已经加载完所有数据');
      return; // 已经加载完所有数据
    }

    const loadingPagination = {
      ...get().pagination,
      isLoading: true
    };

    // 设置分页加载状态
    set(state => ({
      ...state,
      pagination: loadingPagination
    }));

    // 同步更新全局分页状态
    setPagination(loadingPagination, 'noteStore.loadMoreNotes.loading');

    console.log(`[loadMoreNotes] 开始加载第${nextPage}页数据...`);
    try {
      await get().fetchNotes(nextPage, pagination.limit, tags, false, topicId, isPrivate, filterType, null, kbId);
    } catch (error) {
      console.error('[loadMoreNotes] 加载失败:', error);
      // 重置分页加载状态
      set(state => ({
        ...state,
        pagination: {
          ...state.pagination,
          isLoading: false
        }
      }));
    }
  },

  // 获取单个笔记
  fetchNote: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get(`/notes/${id}`);
      set({
        currentNote: response.data,
        isLoading: false,
      });
    } catch (error) {
      set({
        isLoading: false,
        error: error.response?.data?.message || 'Failed to fetch note details',
      });
    }
  },

  // 创建笔记
  createNote: async (noteData) => {
    console.log('[noteStore] createNote 开始');
    console.log('[noteStore] 笔记数据:', noteData);

    set({ isLoading: true, error: null });
    try {
      console.log('[noteStore] 发送 API 请求到 /notes');
      const response = await apiClient.post('/notes', noteData);
      console.log('[noteStore] API 响应:', response);

      // 更新笔记列表
      const { notes, pinnedNotes } = get();
      const newNote = response.data;

      if (newNote.is_pinned) {
        set({
          pinnedNotes: [newNote, ...pinnedNotes],
          isLoading: false,
        });
      } else {
        set({
          notes: [newNote, ...notes],
          isLoading: false,
        });
      }

      // 设置笔记状态变化，触发UI刷新
      setNoteStateChange(newNote.id, 'create', 'noteStore.createNote');

      return { success: true, data: newNote };
    } catch (error) {
      set({
        isLoading: false,
        error: error.response?.data?.message || 'Failed to create note',
      });
      return { success: false, message: error.response?.data?.message || 'Failed to create note' };
    }
  },

  // 更新笔记
  updateNote: async (id, noteData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.put(`/notes/${id}`, noteData);
      const updatedNote = response.data;

      // 更新当前笔记
      if (get().currentNote?.id === id) {
        set({ currentNote: updatedNote });
      }

      // 更新列表中的笔记
      const { notes, pinnedNotes } = get();

      if (updatedNote.is_pinned) {
        set({
          notes: notes.filter(note => note.id !== id),
          pinnedNotes: pinnedNotes.some(note => note.id === id)
            ? pinnedNotes.map(note => note.id === id ? updatedNote : note)
            : [updatedNote, ...pinnedNotes],
          isLoading: false,
        });
      } else {
        set({
          pinnedNotes: pinnedNotes.filter(note => note.id !== id),
          notes: notes.some(note => note.id === id)
            ? notes.map(note => note.id === id ? updatedNote : note)
            : [updatedNote, ...notes],
          isLoading: false,
        });
      }

      // 设置笔记状态变化，触发UI刷新
      setNoteStateChange(id, 'update', 'noteStore.updateNote');

      return { success: true, data: updatedNote };
    } catch (error) {
      set({
        isLoading: false,
        error: error.response?.data?.message || 'Failed to update note',
      });
      return { success: false, message: error.response?.data?.message || 'Failed to update note' };
    }
  },

  // 删除笔记
  deleteNote: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await apiClient.delete(`/notes/${id}`);

      // 从列表中移除笔记
      const { notes, pinnedNotes } = get();
      set({
        notes: notes.filter(note => note.id !== id),
        pinnedNotes: pinnedNotes.filter(note => note.id !== id),
        isLoading: false,
      });

      // 如果当前笔记被删除，清除当前笔记
      if (get().currentNote?.id === id) {
        set({ currentNote: null });
      }

      // 设置笔记状态变化，触发UI刷新
      setNoteStateChange(id, 'delete', 'noteStore.deleteNote');

      return { success: true };
    } catch (error) {
      set({
        isLoading: false,
        error: error.response?.data?.message || 'Failed to delete note',
      });
      return { success: false, message: error.response?.data?.message || 'Failed to delete note' };
    }
  },

  // 置顶/取消置顶笔记
  togglePinNote: async (id, isPinned) => {
    try {
      const endpoint = isPinned ? `/notes/${id}/unpin` : `/notes/${id}/pin`;
      const response = await apiClient.post(endpoint);
      const updatedNote = response.data;

      // 更新列表
      const { notes, pinnedNotes } = get();
      if (updatedNote.is_pinned) {
        set({
          notes: notes.filter(note => note.id !== id),
          pinnedNotes: [updatedNote, ...pinnedNotes.filter(note => note.id !== id)],
        });
      } else {
        set({
          pinnedNotes: pinnedNotes.filter(note => note.id !== id),
          notes: [updatedNote, ...notes.filter(note => note.id !== id)],
        });
      }

      // 更新当前笔记
      if (get().currentNote?.id === id) {
        set({ currentNote: updatedNote });
      }

      // 设置笔记状态变化，触发UI刷新
      const changeType = updatedNote.is_pinned ? 'pin' : 'unpin';
      setNoteStateChange(id, changeType, 'noteStore.togglePinNote');

      return { success: true, data: updatedNote };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || (isPinned ? 'Failed to unpin note' : 'Failed to pin note')
      };
    }
  },
}));